import { ChangeEvent, memo, useCallback } from 'react'
import { Box } from '@/components/Layout'
import { Price, Slider, Text, usePrice } from '@/components/Data'
import { Input } from '@/components/Inputs'
import { useTranslations } from '@/core/translation'
import { SxProps } from '@mui/material'
import { priceRangeStyles as styles } from './PriceRange.style'
import { PriceRangeProps } from './PriceRange.types'

function PriceRange({
  showTitle = true,
  min,
  max,
  value,
  onChange,
  wrapperProps = {},
  textProps = {},
  inputWrapperProps = {},
  minInputProps = {},
  maxInputProps = {},
  sliderProps = {},
}: PriceRangeProps) {
  const t = useTranslations('priceRange')

  const { sx: wrapperSx, ...otherWrapperProps } = wrapperProps
  const { sx: titleSx, ...otherTitleProps } = textProps
  const { sx: inputWrapperSx, ...otherInputWrapperProps } = inputWrapperProps
  const { sx: minInputPropsSx, ...otherMinInputProps } = minInputProps
  const { sx: maxInputPropsSx, ...otherMaxInputProps } = maxInputProps
  const { sx: sliderPropsSx, ...otherSliderPropsProps } = sliderProps

  const { price: minInputValue } = usePrice({ value: value.min || min || 0 })
  const { price: maxInputValue } = usePrice({ value: value.max || max || 0 })

  const parseNumericInput = useCallback((raw: string): number => {
    const clean = raw.replace(/[^0-9.]/g, '')
    if (clean === '') return 0
    const num = parseFloat(clean)
    return Number.isNaN(num) ? 0 : num
  }, [])

  const handleMinChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const newMin = parseNumericInput(e.target.value)
      const currentMax = value.max ?? max ?? 0
      onChange?.({ min: newMin, max: currentMax })
    },
    [max, onChange, parseNumericInput, value.max],
  )

  const handleMaxChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const newMax = parseNumericInput(e.target.value)
      const currentMin = value.min ?? min ?? 0
      onChange?.({ min: currentMin, max: newMax })
    },
    [min, onChange, parseNumericInput, value.min],
  )

  const handleSliderChange = useCallback(
    (_: Event, newValue: number | number[]) => {
      if (Array.isArray(newValue) && newValue.length >= 2) {
        onChange?.({ min: newValue[0], max: newValue[1] })
      }
    },
    [onChange],
  )

  return (
    <Box sx={[styles.wrapper, wrapperSx] as SxProps} {...otherWrapperProps}>
      {showTitle && (
        <Text sx={[styles.title, titleSx] as SxProps} {...otherTitleProps}>
          {t('title')}
        </Text>
      )}
      <Box sx={[styles.inputWrapper, inputWrapperSx] as SxProps} {...otherInputWrapperProps}>
        <Input
          numeric
          value={minInputValue}
          sx={[minInputPropsSx] as SxProps}
          onChange={handleMinChange}
          placeholder={t('min')}
          label={t('min')}
          labelPlacement="outside"
          {...otherMinInputProps}
        />
        <Input
          sx={[maxInputPropsSx] as SxProps}
          value={maxInputValue}
          onChange={handleMaxChange}
          placeholder={t('max')}
          label={t('max')}
          labelPlacement="outside"
          {...otherMaxInputProps}
        />
      </Box>
      <Slider
        sx={[sliderPropsSx] as SxProps}
        value={[Number(value.min || min), Number(value.max || max)]}
        min={Number(min)}
        max={Number(max)}
        valueLabelFormat={(valueLabel) => <ValueLabelFormat value={valueLabel} />}
        onChange={handleSliderChange}
        {...otherSliderPropsProps}
      />
    </Box>
  )
}

export default memo(PriceRange)

function ValueLabelFormat({ value }: { value: number }) {
  return <Price value={value} />
}
